// Moteur de prix SwiftEats — module PUR (aucun accès réseau, aucune dépendance).
// Importé par les edge functions (`price-quote`, `create-order`, via `livraison.ts`) ET par la console
// dispatch (aperçu de l'écran Tarifs) : UN seul calcul. Jumeau SQL : `public.interpoler_prix` /
// `public.prix_livraison` (migration 0054), vérifié par les MÊMES vecteurs (`pricing.vecteurs.json`).
//
// Tous les montants sont en FCFA (entiers). Aucune conversion de devise : XOF natif (Wave / Orange Money Mali).
//
// total = subtotal (articles + suppléments d'options)
//       + delivery_fee (prix progressif `delivery_pricing` + traversée ; 0 si livraison offerte)
//       + service_fee (selon la distance, `fraisServiceProgressif`) + small_order_fee
//       + weather_fee (surcharge météo, `weather_bonuses`)
//       + tension_fee (majoration de forte demande, 0054 — due même si la livraison est offerte)
//       + tip

// ─────────────────────────────────────────────
// Distance (Haversine) — km entre deux points GPS
// ─────────────────────────────────────────────
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371; // rayon terrestre en km
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ─────────────────────────────────────────────
// Barème de livraison (`delivery_pricing`) — PRIX PROGRESSIF (0054)
// ─────────────────────────────────────────────
//
// Avant 0054 : prix par MARCHES (une tranche = un prix fixe ; 0,1 km et 1,4 km coûtaient pareil).
// Depuis : chaque tranche donne le prix À SA LIMITE HAUTE (`radius_max_km`) ; un point à 0 km porte le
// prix MINIMUM de la colonne (`platform_config.livraison_min_*`). Entre deux points : interpolation
// linéaire. Puis arrondi aux 50 F les plus proches (demi vers le haut), et jamais sous le minimum.
// Au-delà de la dernière limite : prix de la dernière tranche (comportement d'avant).
// `radius_min_km` ne sert plus au calcul (il reste dans la table et dans l'écran pour la lisibilité).
// Une grille NON croissante (prix qui baisse avec la distance) est interpolée telle quelle, sans
// erreur : c'est l'écran Tarifs qui refuse d'en ENREGISTRER une.

export interface DeliveryTier {
  radius_min_km: number;
  radius_max_km: number;
  client_fee: number;
  driver_pay: number;
  premium_fee: number;
}

/** Prix à 0 km de chaque colonne (planchers). `premium` : inutilisé depuis 0055 (Premium dérivé du prix client). */
export interface MinimaLivraison {
  client: number;
  premium: number;
  driver: number;
}

/** Valeurs de la migration 0054 (décision du 12/10/2026) — replis si la colonne manque. */
export const MINIMA_PAR_DEFAUT: MinimaLivraison = { client: 1000, premium: 400, driver: 650 };

/** Prix de livraison à une distance donnée, par colonne. */
export interface PrixLivraison {
  /** Ce que paie un client ordinaire. */
  client: number;
  /** Ce que paie un abonné Premium : prix client × (1 − réduction), arrondi 50 F (0055). */
  premium: number;
  /** Ce que touche le livreur. */
  driver: number;
}

export interface WeatherBonus {
  condition: string;
  client_surcharge: number;
  driver_bonus: number;
  premium_exempt: boolean;
}

/**
 * Arrondi aux 50 F les plus proches, demi vers le haut (1 025 → 1 050).
 * Le `1e-9` absorbe le bruit flottant (1 024,999 999 99 lu pour 1 025) : le jumeau SQL calcule en
 * `numeric` exact, les deux doivent tomber pareil.
 */
export function arrondi50(v: number): number {
  return Math.floor(v / 50 + 0.5 + 1e-9) * 50;
}

/** Un point de la courbe : (distance, prix). */
export interface PointPrix {
  km: number;
  prix: number;
}

/**
 * Prix d'UNE colonne à `km`. `points` = les limites hautes des tranches, déjà classées (le point à
 * 0 km est ajouté ici, avec `minimum`). Distance absente, négative ou illisible → 0 km.
 */
export function interpolerPrix(points: PointPrix[], minimum: number, km: number): number {
  const d = Number.isFinite(km) && km > 0 ? km : 0;
  let x0 = 0;
  let y0 = minimum;
  let brut: number | null = null;
  if (d <= 0) brut = minimum;
  else {
    for (const p of points) {
      if (!(p.km > 0)) continue; // une limite à 0 km n'apporte rien (et diviserait par zéro)
      if (d <= p.km) {
        brut = p.km > x0 ? y0 + ((p.prix - y0) * (d - x0)) / (p.km - x0) : p.prix;
        break;
      }
      x0 = p.km;
      y0 = p.prix;
    }
    if (brut === null) brut = y0; // au-delà de la dernière limite : dernier prix
  }
  return Math.max(minimum, arrondi50(brut));
}

/**
 * Les tranches classées comme le jumeau SQL : limite haute, puis limite basse (tri STABLE — les
 * edge functions lisent la table triée par `id` en plus, comme `order by …, id` côté SQL).
 */
export function trierParLimiteHaute<T extends { radius_min_km: number; radius_max_km: number }>(tiers: T[]): T[] {
  return [...tiers].sort((a, b) =>
    Number(a.radius_max_km) - Number(b.radius_max_km) || Number(a.radius_min_km) - Number(b.radius_min_km)
  );
}

/** Réduction Premium par défaut (0055) : la livraison à moitié prix. */
export const REDUCTION_PREMIUM_PAR_DEFAUT = 50;

/**
 * Frais Premium (0055) = arrondi 50 F de (frais client progressifs × (1 − réduction %)). La colonne
 * `premium_fee` de la grille et `minima.premium` ne servent plus au calcul (gardés pour l'historique).
 * Jumeau SQL : `public.prix_premium`.
 */
export function prixPremium(client: number, reductionPct: number): number {
  const r = Math.min(100, Math.max(0, Number.isFinite(reductionPct) ? reductionPct : 0));
  return arrondi50((client * (100 - r)) / 100);
}

/** Prix progressif des trois colonnes à `km`. Grille vide → les minima. */
export function prixProgressif(
  tiers: DeliveryTier[],
  minima: MinimaLivraison,
  km: number,
  reductionPremiumPct: number = REDUCTION_PREMIUM_PAR_DEFAUT,
): PrixLivraison {
  const tries = trierParLimiteHaute(tiers);
  const col = (champ: "client_fee" | "driver_pay") =>
    tries.map((t) => ({ km: Number(t.radius_max_km), prix: Number(t[champ]) }));
  const client = interpolerPrix(col("client_fee"), minima.client, km);
  return {
    client,
    premium: prixPremium(client, reductionPremiumPct),
    driver: interpolerPrix(col("driver_pay"), minima.driver, km),
  };
}

// ─────────────────────────────────────────────
// Frais de service selon la distance (complément 0054)
// ─────────────────────────────────────────────
//
// Deux réglages : limite BASSE = `platform_config.service_fee` (frais à 0 km) et limite HAUTE =
// `service_fee_haut` (frais à la distance maximale de la grille de livraison, 18 km en prod). Entre les
// deux : interpolation linéaire, arrondi aux 25 F les plus proches (demi vers le haut) ; au-delà de la
// distance maximale : la limite haute. `service_fee_haut` NULL (ou égal à la limite basse) = frais FIXES,
// exactement la valeur actuelle (aucun arrondi) : rien ne change tant que le dispatch ne règle rien.
// Ils restent dus quand la livraison est offerte (comme avant).

/** Arrondi aux 25 F les plus proches, demi vers le haut (même tolérance flottante que `arrondi50`). */
export function arrondi25(v: number): number {
  return Math.floor(v / 25 + 0.5 + 1e-9) * 25;
}

/** Distance maximale de la grille (plus grande limite haute) ; 0 si grille vide. */
export function distanceMaxGrille(tiers: Pick<DeliveryTier, "radius_max_km">[]): number {
  return tiers.reduce((m, t) => Math.max(m, Number(t.radius_max_km) || 0), 0);
}

export function fraisServiceProgressif(
  bas: number,
  haut: number | null | undefined,
  distanceMaxKm: number,
  km: number,
): number {
  if (haut == null || !Number.isFinite(haut) || haut === bas) return bas;
  const d = Number.isFinite(km) && km > 0 ? km : 0;
  if (!(distanceMaxKm > 0)) return bas;
  const part = Math.min(d, distanceMaxKm) / distanceMaxKm;
  return arrondi25(bas + (haut - bas) * part);
}

// ─────────────────────────────────────────────
// Majoration de tension (forte demande dans la zone du restaurant, 0054)
// ─────────────────────────────────────────────
//
// demande = commandes payées sans livreur dont le restaurant est à moins de R km ;
// offre    = livreurs `available`, actifs, position de moins de 5 min à moins de R km
// (comptées par la fonction SQL `tension_zone`). ratio = demande / max(offre, 1).
// majoration % = clamp((ratio − seuil) × pente, 0, plafond), arrondie au centième.
// Montant = arrondi aux 50 F de (frais de livraison progressifs × %), part livreur = arrondi au franc.

export interface ReglagesTension {
  active: boolean;
  rayonKm: number;
  seuil: number;
  /** Points de % par point de ratio au-dessus du seuil. */
  pentePct: number;
  plafondPct: number;
  /** Part de la majoration reversée au livreur, en %. */
  partLivreurPct: number;
}

/** Valeurs de la migration 0054 : DÉSACTIVÉE par défaut. */
export const TENSION_PAR_DEFAUT: ReglagesTension = {
  active: false,
  rayonKm: 3,
  seuil: 1,
  pentePct: 20,
  plafondPct: 30,
  partLivreurPct: 80,
};

const borne = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export function ratioTension(demande: number, offre: number): number {
  const dem = Number.isFinite(demande) && demande > 0 ? demande : 0;
  const off = Number.isFinite(offre) && offre > 1 ? offre : 1;
  return dem / off;
}

/** Majoration en % (0 si désactivée), au centième. */
export function majorationTensionPct(demande: number, offre: number, r: ReglagesTension): number {
  if (!r.active) return 0;
  const plafond = borne(Number(r.plafondPct) || 0, 0, 100);
  const brut = (ratioTension(demande, offre) - (Number(r.seuil) || 0)) * (Number(r.pentePct) || 0);
  const pct = borne(Number.isFinite(brut) ? brut : 0, 0, plafond);
  return Math.round(pct * 100) / 100;
}

/** Montant de la majoration et part du livreur, à partir des frais de livraison progressifs. */
export function montantTension(
  baseLivraison: number,
  pct: number,
  partLivreurPct: number,
): { fee: number; driver: number } {
  if (!(pct > 0) || !(baseLivraison > 0)) return { fee: 0, driver: 0 };
  const fee = arrondi50((baseLivraison * pct) / 100);
  const part = borne(Number(partLivreurPct) || 0, 0, 100);
  return { fee, driver: Math.round((fee * part) / 100) };
}

// ─────────────────────────────────────────────
// Ventilation livraison d'une commande
// ─────────────────────────────────────────────

export interface DeliveryResult {
  distanceKm: number;
  deliveryFee: number; // frais de livraison facturés au client (traversée comprise ; 0 si offerte)
  weatherFee: number; // surcharge météo (colonne orders.weather_fee)
  tensionFee: number; // majoration de forte demande payée par le client (orders.tension_fee ; 0 si Premium)
  tensionPct: number; // % appliqué (orders.tension_pct)
  tensionDriverPay: number; // part livreur de la majoration (orders.tension_driver_pay, INCLUSE dans driverPay)
  driverPay: number; // rémunération livreur (orders.driver_pay) : progressif + météo + traversée + tension
  freeDelivery: boolean;
}

// `isPremium` (0055) : livraison = `prix.premium` (−50 %), météo neutralisée (ni supplément ni bonus livreur),
// pas de majoration de tension pour le client (le livreur en garde sa part, supportée par SwiftEats).
// `subtotal >= freeDeliveryThreshold` → livraison (et traversée) offerte ; météo et tension restent dues,
// le livreur touche tout.
export function computeDelivery(opts: {
  distanceKm: number;
  isPremium: boolean;
  subtotal: number;
  freeDeliveryThreshold: number;
  /** Prix progressif à la distance (`prixProgressif`) ; null = aucun barème (tout à 0). */
  prix: PrixLivraison | null;
  weather: WeatherBonus | null;
  /** Supplément « traversée du fleuve » (null = même rive ou position inconnue). */
  traversee?: { frais: number; bonus: number } | null;
  /** Majoration de tension en % (0 / absent = aucune) et part livreur en %. */
  tension?: { pct: number; partLivreurPct: number } | null;
}): DeliveryResult {
  const { distanceKm, isPremium, subtotal, freeDeliveryThreshold, prix, weather } = opts;
  const traversee = opts.traversee ?? null;

  const baseClientFee = prix ? (isPremium ? prix.premium : prix.client) : 0;
  const baseDriverPay = prix ? prix.driver : 0;

  // Météo (0055, décision finale) : sur une commande Premium la météo est NEUTRALISÉE — ni supplément client, ni
  // bonus livreur, quelle que soit `weather_bonuses.premium_exempt` (ignorée). Rien à supporter, aucune écriture
  // compensatoire. `driver_pay` (montant montré dans l'offre de l'app Coursier) le reflète avant acceptation.
  const meteoNeutralisee = isPremium;
  const weatherFee = meteoNeutralisee || !weather ? 0 : Number(weather.client_surcharge) || 0;
  const driverBonus = meteoNeutralisee || !weather ? 0 : Number(weather.driver_bonus) || 0;

  const freeDelivery = subtotal > 0 && subtotal >= freeDeliveryThreshold;
  // La traversée fait partie de la livraison : offerte quand la livraison
  // l'est. Le bonus du livreur, lui, reste dû — c'est lui qui fait le détour.
  const deliveryFee = freeDelivery ? 0 : baseClientFee + (traversee?.frais ?? 0);

  // Assiette de la tension = frais progressifs du client ORDINAIRE (sans traversée ni météo), même si la
  // livraison est offerte : la forte demande se paie comme la pluie. Un client Premium ne la paie pas (0055),
  // mais le livreur touche quand même sa part : SwiftEats la supporte.
  const tensionPct = opts.tension && opts.tension.pct > 0 ? opts.tension.pct : 0;
  const t = montantTension(prix ? prix.client : 0, tensionPct, opts.tension?.partLivreurPct ?? 0);

  return {
    distanceKm: Math.round(distanceKm * 10) / 10,
    deliveryFee,
    weatherFee,
    tensionFee: isPremium ? 0 : t.fee,
    tensionPct: t.fee > 0 || t.driver > 0 ? tensionPct : 0,
    tensionDriverPay: t.driver,
    driverPay: baseDriverPay + driverBonus + (traversee?.bonus ?? 0) + t.driver,
    freeDelivery,
  };
}
